import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppstoreOutlined,
  CloseOutlined,
  EyeOutlined,
  FileSearchOutlined,
  FolderOutlined,
  MailOutlined,
  MoreOutlined,
  RollbackOutlined,
  SearchOutlined,
  SolutionOutlined,
  TeamOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Input, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TablePaginationConfig } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useProfile } from '../../profile/components/ProfileContext';
import {
  JobApplicationService,
  jobApplicationScopeFilter,
} from '../services/job-application.service';
import { JobApplication } from '../models/job-application.model';
import { UtilService } from '../../../../../core/services/util.service';
import { route } from '../../../../../constants/routes';
import { formatApplicationSkillScore } from '../utils/application-relevance-labels';
import JobApplicationViewToggle, {
  JobApplicationViewMode,
  readJobApplicationsViewMode,
} from './JobApplicationViewToggle';
import JobApplicationPipelineBoard from './JobApplicationPipelineBoard';
import {
  PipelineColumn,
  buildStandardPipelineColumns,
  normalizeStageKey,
} from '../utils/pipeline-board.utils';
import { JobService } from '../../job/services/job.service';
import { StagesService } from '../../company/services/stages.service';
import { StageId } from '../../company/models/stages.model';
import { recruiterName } from '../utils/application-recruiter';
import '../../company/components/Company.css';
import '../../job/components/JobDirectory.css';

interface Props {
  action: boolean;
}

export interface JobPostTableRef {
  refresh: () => Promise<void>;
}

type ApplicationTab = 'active' | 'archived';

const BOARD_PAGE_SIZE = 100;

const candidateName = (application: JobApplication) =>
  (typeof application.candidate === 'string' && application.candidate) ||
  application.fullname ||
  `${application.firstname || ''} ${application.lastname || ''}`.trim() ||
  'Unnamed candidate';

const candidateInitials = (application: JobApplication) =>
  candidateName(application)
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

const applicationJobName = (application: JobApplication) =>
  application.jobName ||
  application.job?.jobName ||
  application.job?.name ||
  'Job not provided';

const applicationClient = (application: JobApplication) =>
  application.client ||
  (typeof application.job?.client === 'string'
    ? application.job.client
    : application.job?.client?.name) ||
  'Client not provided';

const applicationStage = (application: JobApplication) =>
  (application.status || 'RECEIVED').replace(/_/g, ' ');

const titleCase = (value: string) =>
  value
    .toLowerCase()
    .split(/\s+/)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');

const stageTone = (stage: string) => {
  const normalized = stage.toUpperCase();
  if (normalized.includes('OFFER') || normalized.includes('ACCEPTED')) {
    return 'offer';
  }
  if (
    normalized.includes('RECEIVED') ||
    normalized.includes('WAIT') ||
    normalized.includes('FOLLOW')
  ) {
    return 'waiting';
  }
  if (normalized.includes('REJECT') || normalized.includes('CLOSED')) {
    return 'archived';
  }
  return 'active';
};

const formatDate = (value: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const JobApplicationTable = forwardRef<JobPostTableRef, Props>(
  ({ action }, ref) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { companyId, profile } = useProfile();
    const [activeData, setActiveData] = useState<JobApplication[]>([]);
    const [archivedData, setArchivedData] = useState<JobApplication[]>([]);
    const [pagination, setPagination] = useState({
      current: 1,
      pageSize: 10,
    });
    const [activeTotal, setActiveTotal] = useState(0);
    const [archivedTotal, setArchivedTotal] = useState(0);
    const [sort, setSort] = useState<string>();
    const [activeTab, setActiveTab] = useState<ApplicationTab>('active');
    const [viewMode, setViewMode] = useState<JobApplicationViewMode>(
      () => readJobApplicationsViewMode(location.state) ?? 'table'
    );
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [boardLoading, setBoardLoading] = useState(false);
    const [selectedApplication, setSelectedApplication] =
      useState<JobApplication | null>(null);
    const [selectedApplicationLoading, setSelectedApplicationLoading] =
      useState(false);
    const [
      selectedApplicationDetailsUnavailable,
      setSelectedApplicationDetailsUnavailable,
    ] = useState(false);
    const countsFetchedRef = useRef(false);
    const didInitialLoad = useRef(false);
    const previewRequestRef = useRef(0);
    const stagesByClientRef = useRef<Map<string, StageId[]>>(new Map());
    const clientByApplicationRef = useRef<Map<string, string>>(new Map());

    useEffect(() => {
      const restored = readJobApplicationsViewMode(location.state);
      if (restored) setViewMode(restored);
    }, [location.key, location.state]);

    const fetchTabData = useCallback(
      async (tabType: ApplicationTab, fetchDataToo = true) => {
        if (!companyId) return;
        const deletedFilter = jobApplicationScopeFilter(tabType);
        const useBoardFetch =
          fetchDataToo &&
          tabType === 'active' &&
          viewMode === 'board' &&
          activeTab === 'active';

        if (fetchDataToo) {
          if (useBoardFetch) setBoardLoading(true);
          else setLoading(true);
        }

        try {
          const params = fetchDataToo
            ? {
                page: useBoardFetch ? 1 : pagination.current,
                size: useBoardFetch ? BOARD_PAGE_SIZE : pagination.pageSize,
                ...(sort ? { sort } : {}),
                ...(tabType === 'archived' && !sort
                  ? { sort: '-appliedOn' }
                  : {}),
                filter: deletedFilter,
              }
            : {
                page: 1,
                size: 1,
                filter: deletedFilter,
              };
          const result =
            tabType === 'active'
              ? await JobApplicationService.GetActiveApplications(params)
              : await JobApplicationService.GetApplications(companyId, params);
          const content = result?.content || [];
          const total = result?.totalElements || 0;

          if (fetchDataToo) {
            if (tabType === 'active') {
              setActiveData(content);
              setActiveTotal(total);
            } else {
              setArchivedData(content);
              setArchivedTotal(total);
            }
          } else if (tabType === 'active') {
            setActiveTotal(total);
          } else {
            setArchivedTotal(total);
          }
        } catch (error) {
          console.error(`Failed to fetch ${tabType} job applications:`, error);
          UtilService.Alert(
            'Applications unavailable',
            'error',
            'We could not load this part of the hiring pipeline. Please try again.'
          );
        } finally {
          if (fetchDataToo) {
            if (useBoardFetch) setBoardLoading(false);
            else setLoading(false);
          }
        }
      },
      [activeTab, companyId, pagination, sort, viewMode]
    );

    const fetchInitialData = useCallback(async () => {
      if (!companyId || countsFetchedRef.current) return;
      await Promise.all([
        fetchTabData('active', true),
        fetchTabData('archived', false),
      ]);
      countsFetchedRef.current = true;
    }, [companyId, fetchTabData]);

    useEffect(() => {
      if (companyId && !countsFetchedRef.current && !didInitialLoad.current) {
        void fetchInitialData();
        didInitialLoad.current = true;
      }
    }, [companyId, fetchInitialData]);

    useEffect(() => {
      if (didInitialLoad.current && companyId && countsFetchedRef.current) {
        void fetchTabData(activeTab, true);
      }
    }, [activeTab, companyId, fetchTabData, pagination, sort, viewMode]);

    const closeApplicationPreview = () => {
      previewRequestRef.current += 1;
      setSelectedApplication(null);
      setSelectedApplicationLoading(false);
      setSelectedApplicationDetailsUnavailable(false);
    };

    useImperativeHandle(ref, () => ({
      refresh: async () => {
        setPagination({ current: 1, pageSize: 10 });
        setSort(undefined);
        setSearchTerm('');
        closeApplicationPreview();
        countsFetchedRef.current = false;
        didInitialLoad.current = false;
        await fetchInitialData();
        didInitialLoad.current = true;
      },
    }));

    const currentData = useMemo(
      () => (activeTab === 'active' ? activeData : archivedData),
      [activeData, activeTab, archivedData]
    );

    const signedInRecruiter = useMemo(
      () =>
        profile?.fullname?.trim() ||
        `${profile?.firstname || ''} ${profile?.lastname || ''}`.trim(),
      [profile?.firstname, profile?.fullname, profile?.lastname]
    );

    const applicationRecruiter = useCallback(
      (application: JobApplication) =>
        recruiterName(
          application,
          activeTab === 'active' ? signedInRecruiter : undefined
        ),
      [activeTab, signedInRecruiter]
    );

    const visibleApplications = useMemo(() => {
      const normalized = searchTerm.trim().toLowerCase();
      if (!normalized) return currentData;
      return currentData.filter((application) =>
        [
          candidateName(application),
          application.email,
          applicationJobName(application),
          applicationClient(application),
          applicationRecruiter(application),
          applicationStage(application),
        ].some((value) => value?.toLowerCase().includes(normalized))
      );
    }, [applicationRecruiter, currentData, searchTerm]);

    const openApplication = (application: JobApplication) => {
      navigate(route(`/job-applications/${application.id}`), {
        state: {
          jobName: applicationJobName(application),
          candidateName: candidateName(application),
          fromJobApplicationsTable: true,
          jobApplicationsViewMode: viewMode,
        },
      });
    };

    const openJob = (application: JobApplication) => {
      if (!application.job?.id) return;
      navigate(route(`/job/${application.job.id}`), {
        state: { jobName: applicationJobName(application) },
      });
    };

    const restoreApplication = async (application: JobApplication) => {
      try {
        await JobApplicationService.Restore(application.id);
        closeApplicationPreview();
        await Promise.all([
          fetchTabData('active', true),
          fetchTabData('archived', true),
        ]);
        UtilService.Alert(
          'Application restored',
          'success',
          `${candidateName(application)} is available in the active applications.`
        );
      } catch (error) {
        console.error('Error restoring application:', error);
        UtilService.Alert(
          'Restore unavailable',
          'error',
          'The application could not be moved back to Active.'
        );
      }
    };

    const openApplicationPreview = async (application: JobApplication) => {
      const requestId = previewRequestRef.current + 1;
      previewRequestRef.current = requestId;
      setSelectedApplication(application);
      setSelectedApplicationLoading(true);
      setSelectedApplicationDetailsUnavailable(false);

      try {
        const details = await JobApplicationService.GetById(application.id);
        if (previewRequestRef.current !== requestId) return;
        setSelectedApplication({ ...application, ...details });
      } catch (error) {
        if (previewRequestRef.current !== requestId) return;
        console.error('Failed to load application details:', error);
        setSelectedApplicationDetailsUnavailable(true);
      } finally {
        if (previewRequestRef.current === requestId) {
          setSelectedApplicationLoading(false);
        }
      }
    };

    const onChange = (
      paginationConfig: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<JobApplication> | SorterResult<JobApplication>[]
    ) => {
      if (!Array.isArray(sorter) && sorter.order && sorter.field) {
        const direction = sorter.order === 'ascend' ? '' : '-';
        setSort(`${direction}${String(sorter.field)}`);
      } else {
        setSort(undefined);
      }
      setPagination({
        current: paginationConfig.current || 1,
        pageSize: paginationConfig.pageSize || 10,
      });
    };

    const columns: ColumnsType<JobApplication> = [
      {
        title: 'Candidate',
        dataIndex: 'candidate',
        key: 'candidate',
        sorter: true,
        width: 235,
        render: (_, application) => (
          <div className="company-directory__identity-cell">
            <span className="company-directory__avatar application-directory__candidate-avatar">
              {candidateInitials(application)}
            </span>
            <span>
              <strong>{candidateName(application)}</strong>
              <small>{application.email || 'Email not provided'}</small>
            </span>
          </div>
        ),
      },
      {
        title: 'Job',
        dataIndex: 'jobName',
        key: 'jobName',
        sorter: true,
        width: 220,
        render: (_, application) => (
          <div className="company-directory__stacked-cell">
            <strong>{applicationJobName(application)}</strong>
            <small>{applicationClient(application)}</small>
          </div>
        ),
      },
      {
        title: 'Recruiter',
        key: 'recruiter',
        width: 160,
        render: (_, application) => applicationRecruiter(application),
      },
      {
        title: 'Stage',
        dataIndex: 'status',
        key: 'status',
        sorter: true,
        width: 135,
        render: (_, application) => {
          const stage = applicationStage(application);
          return (
            <Tag
              className={`application-directory__stage application-directory__stage--${stageTone(
                stage
              )}`}
            >
              {titleCase(stage)}
            </Tag>
          );
        },
      },
      {
        title: 'Skill match',
        dataIndex: 'relevance',
        key: 'relevance',
        width: 165,
        render: (relevance: number) => (
          <span className="application-directory__score">
            {formatApplicationSkillScore(relevance) || 'Not scored'}
          </span>
        ),
      },
      {
        title: 'Applied',
        dataIndex: 'appliedOn',
        key: 'appliedOn',
        sorter: true,
        width: 135,
        render: (value: string) => formatDate(value),
      },
      ...(action
        ? [
            {
              title: 'Actions',
              key: 'actions',
              align: 'right' as const,
              width: 90,
              render: (_: unknown, application: JobApplication) => (
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: [
                      {
                        key: 'view',
                        icon: <EyeOutlined />,
                        label: 'Open application',
                      },
                      ...(application.job?.id
                        ? [
                            {
                              key: 'job',
                              icon: <AppstoreOutlined />,
                              label: 'View job',
                            },
                          ]
                        : []),
                      ...(activeTab === 'archived'
                        ? [
                            {
                              key: 'restore',
                              icon: <RollbackOutlined />,
                              label: 'Move to active',
                            },
                          ]
                        : []),
                    ],
                    onClick: ({ key, domEvent }) => {
                      domEvent.stopPropagation();
                      if (key === 'view') openApplication(application);
                      if (key === 'job') openJob(application);
                      if (key === 'restore')
                        void restoreApplication(application);
                    },
                  }}
                >
                  <Button
                    type="text"
                    className="company-directory__more"
                    aria-label={`Actions for ${candidateName(application)}`}
                    icon={<MoreOutlined />}
                    onClick={(event) => event.stopPropagation()}
                  />
                </Dropdown>
              ),
            },
          ]
        : []),
    ];

    const pipelineColumns = useMemo(() => buildStandardPipelineColumns(), []);
    const showBoard = viewMode === 'board' && activeTab === 'active';

    const resolvePipelineMoveTarget = useCallback(
      async (
        application: JobApplication,
        targetColumn: PipelineColumn
      ): Promise<{ targetColumn: PipelineColumn; clientId: string } | null> => {
        if (!companyId) {
          return null;
        }

        try {
          let clientId =
            clientByApplicationRef.current.get(application.id) ||
            application.job?.clientId ||
            application.job?.client?.id ||
            '';
          let jobId = application.job?.id || '';

          if (!clientId) {
            const details = await JobApplicationService.GetById(application.id);
            clientId = details.job?.clientId || details.job?.client?.id || '';
            jobId = details.job?.id || jobId;
          }

          if (!clientId && jobId) {
            const job = await JobService.GetJob(jobId);
            clientId = job.clientId || job.client?.id || '';
          }

          if (!clientId) {
            throw new Error('Application client is unavailable.');
          }
          clientByApplicationRef.current.set(application.id, clientId);

          let stages = stagesByClientRef.current.get(clientId);
          if (!stages) {
            const result = await StagesService.GetStages(companyId, clientId, {
              page: 1,
              size: 100,
              sort: 'statusOrder',
              filter: 'deleted:eq:false',
            });
            stages = result.content ?? [];
            stagesByClientRef.current.set(clientId, stages);
          }

          const stage = stages.find(
            (item) =>
              normalizeStageKey(item.name || '') === targetColumn.key &&
              Boolean(item.id)
          );
          if (!stage?.id) {
            throw new Error(
              `${targetColumn.label} is not configured for this client.`
            );
          }

          return {
            clientId,
            targetColumn: {
              ...targetColumn,
              id: stage.id,
              stageId: stage.id,
              statusOrder: stage.statusOrder,
            },
          };
        } catch (error) {
          console.error('Failed to resolve pipeline move target:', error);
          UtilService.Alert(
            'Stage unavailable',
            'error',
            error instanceof Error
              ? error.message
              : 'Could not load this client’s hiring stages.'
          );
          return null;
        }
      },
      [companyId]
    );

    const scores = activeData
      .map((application) => Number(application.relevance))
      .filter((score) => Number.isFinite(score));
    const averageScoreOnPage = scores.length
      ? (
          scores.reduce((total, score) => total + score, 0) / scores.length
        ).toFixed(1)
      : '—';
    const interviewsOnPage = activeData.filter((application) =>
      applicationStage(application).toUpperCase().includes('INTERVIEW')
    ).length;

    const renderTable = () => (
      <Table<JobApplication>
        className="company-directory__table"
        columns={columns}
        dataSource={visibleApplications}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: activeTab === 'active' ? activeTotal : archivedTotal,
          showSizeChanger: true,
          showTotal: (total) => `${total} applications`,
          onChange: (current, pageSize) => setPagination({ current, pageSize }),
          onShowSizeChange: (current, pageSize) =>
            setPagination({ current, pageSize }),
        }}
        onChange={onChange}
        onRow={(application) => ({
          onClick: () => void openApplicationPreview(application),
        })}
        rowClassName={(application) =>
          selectedApplication?.id === application.id
            ? 'company-directory__row--previewed'
            : ''
        }
        scroll={{ x: 1050 }}
      />
    );

    return (
      <main className="company-directory application-directory">
        <header className="company-directory__header">
          <div>
            <span>Hiring pipeline</span>
            <h2>Job Applications</h2>
            <p>Review candidates, monitor stages, and keep work moving.</p>
          </div>
        </header>

        <section
          className="company-directory__metrics"
          aria-label="Application totals"
        >
          <article>
            <span className="company-directory__metric-icon">
              <TeamOutlined aria-hidden />
            </span>
            <div>
              <span>Active applications</span>
              <strong>{activeTotal}</strong>
            </div>
          </article>
          <article>
            <span className="company-directory__metric-icon">
              <SolutionOutlined aria-hidden />
            </span>
            <div>
              <span>Interviews on page</span>
              <strong>{interviewsOnPage}</strong>
            </div>
          </article>
          <article>
            <span className="company-directory__metric-icon">
              <FileSearchOutlined aria-hidden />
            </span>
            <div>
              <span>Avg skill score on page</span>
              <strong>{averageScoreOnPage}</strong>
            </div>
          </article>
          <article>
            <span className="company-directory__metric-icon company-directory__metric-icon--muted">
              <FolderOutlined aria-hidden />
            </span>
            <div>
              <span>Archived</span>
              <strong>{archivedTotal}</strong>
            </div>
          </article>
        </section>

        <div
          className={`company-directory__layout${
            selectedApplication ? ' company-directory__layout--preview' : ''
          }`}
        >
          <section className="company-directory__panel">
            <div className="application-directory__toolbar">
              <JobApplicationViewToggle
                value={viewMode}
                boardDisabled={activeTab === 'archived'}
                onChange={(mode) => {
                  setViewMode(mode);
                  closeApplicationPreview();
                }}
              />
              <Input
                allowClear
                prefix={<SearchOutlined aria-hidden />}
                placeholder="Search this page by candidate, job, or client"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              <div className="application-directory__toolbar-meta">
                <span
                  className="company-directory__result-count"
                  aria-live="polite"
                >
                  {visibleApplications.length} shown
                </span>
              </div>
            </div>

            <div
              className="application-directory__scope"
              role="tablist"
              aria-label="Application scope"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'active'}
                className={activeTab === 'active' ? 'is-active' : ''}
                onClick={() => {
                  setActiveTab('active');
                  setPagination((current) => ({ ...current, current: 1 }));
                  closeApplicationPreview();
                }}
              >
                Active <b>{activeTotal}</b>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'archived'}
                className={activeTab === 'archived' ? 'is-active' : ''}
                onClick={() => {
                  setActiveTab('archived');
                  setViewMode('table');
                  setPagination((current) => ({ ...current, current: 1 }));
                  closeApplicationPreview();
                }}
              >
                Archived <b>{archivedTotal}</b>
              </button>
            </div>

            {showBoard ? (
              <>
                <p className="application-directory__hint">
                  Drag candidates between active stages. Closed and custom
                  stages are not shown.
                </p>
                <div className="application-directory__board">
                  <JobApplicationPipelineBoard
                    applications={visibleApplications}
                    columns={pipelineColumns}
                    loading={boardLoading}
                    enableDrag
                    companyId={companyId ?? undefined}
                    resolveMoveTarget={resolvePipelineMoveTarget}
                    onApplicationClick={openApplication}
                    onRefresh={() => fetchTabData('active', true)}
                  />
                </div>
              </>
            ) : (
              renderTable()
            )}
          </section>

          {selectedApplication ? (
            <aside
              className="company-directory__preview"
              aria-label="Application preview"
            >
              <div className="company-directory__preview-header">
                <strong>Application preview</strong>
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  aria-label="Close application preview"
                  onClick={closeApplicationPreview}
                />
              </div>
              <div className="company-directory__preview-body">
                <div className="company-directory__preview-identity application-directory__preview-identity">
                  <span
                    className="company-directory__preview-avatar application-directory__candidate-avatar"
                    aria-hidden
                  >
                    {candidateInitials(selectedApplication)}
                  </span>
                  <div>
                    <h3>{candidateName(selectedApplication)}</h3>
                    <span>
                      {selectedApplication.email || 'Email not provided'}
                    </span>
                    <Tag
                      className={`application-directory__stage application-directory__stage--${stageTone(
                        applicationStage(selectedApplication)
                      )}`}
                    >
                      {titleCase(applicationStage(selectedApplication))}
                    </Tag>
                  </div>
                </div>

                {selectedApplicationLoading ? (
                  <div
                    className="company-directory__preview-loading"
                    role="status"
                  >
                    Loading application details…
                  </div>
                ) : selectedApplicationDetailsUnavailable ? (
                  <div
                    className="company-directory__preview-loading"
                    role="status"
                  >
                    Application details are temporarily unavailable.
                  </div>
                ) : null}

                <div className="company-directory__preview-actions">
                  {selectedApplication.email ? (
                    <a href={`mailto:${selectedApplication.email}`}>
                      <MailOutlined aria-hidden />
                      Email
                    </a>
                  ) : null}
                  {selectedApplication.phoneNumber ? (
                    <a
                      href={`https://wa.me/${selectedApplication.phoneNumber.replace(
                        /\D/g,
                        ''
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <WhatsAppOutlined aria-hidden />
                      WhatsApp
                    </a>
                  ) : null}
                </div>

                <section className="company-directory__preview-section">
                  <h4>Application details</h4>
                  <dl className="company-directory__details">
                    <div>
                      <dt>Job</dt>
                      <dd>{applicationJobName(selectedApplication)}</dd>
                    </div>
                    <div>
                      <dt>Client</dt>
                      <dd>{applicationClient(selectedApplication)}</dd>
                    </div>
                    <div>
                      <dt>Recruiter</dt>
                      <dd>{applicationRecruiter(selectedApplication)}</dd>
                    </div>
                    <div>
                      <dt>Skill score</dt>
                      <dd>
                        {formatApplicationSkillScore(
                          selectedApplication.relevance
                        ) || 'Not scored'}
                      </dd>
                    </div>
                    <div>
                      <dt>Applied</dt>
                      <dd>{formatDate(selectedApplication.appliedOn)}</dd>
                    </div>
                  </dl>
                </section>
              </div>

              <div className="job-directory__preview-footer">
                <button
                  className="company-primary-action"
                  type="button"
                  onClick={() => openApplication(selectedApplication)}
                >
                  <EyeOutlined aria-hidden />
                  Open application
                </button>
                {selectedApplication.job?.id ? (
                  <button
                    className="company-secondary-action"
                    type="button"
                    onClick={() => openJob(selectedApplication)}
                  >
                    <AppstoreOutlined aria-hidden />
                    View job
                  </button>
                ) : null}
              </div>
            </aside>
          ) : null}
        </div>
      </main>
    );
  }
);

export default JobApplicationTable;
